import Vue from 'vue'

import Router from 'vue-router'
import Home from './views/HomePage'
import AccessDenied from './views/AccessDenied'
import EventLog from './views/EventListPage'
import Users from './views/UsersPage'
import UserDetails from './views/UsersPage/UserDetails'
import Licenses from './views/LicensePage'
import Orders from './views/OrdersPage'
import OrderDetails from './views/OrdersPage/OrderDetails'
import OidcCallback from './views/OidcCallback.vue'
import Organizations from './views/OrganizationsPage'
import OrganizationDetails from './views/OrganizationsPage/OrganizationDetails.vue'
import OidcCallbackError from './views/OidcCallbackError.vue'
import ReportsPage from './views/ReportsPage'
import Products from './views/ProductPage'
import ProductDetails from './views/ProductPage/ProductDetails'
import LicenseDetails  from './views/LicensePage/LicenseDetails'
import { vuexOidcCreateRouterMiddleware } from 'vuex-oidc'
import store from './store/store'
import LogOut from './views/LogOutPage'
import Groups from './views/GroupsPage'
import GroupDetails from './views/GroupsPage/GroupDetails'
import ReportView from './views/ReportsPage/Reports'
import BokbasenOrders from './views/ReportsPage/BokbasenOrders'
import BiblioOnixImport from './views/ReportsPage/BiblioOnixImport'
import ScheduledJobs from './views/ReportsPage/ScheduledJobs'
import Reconciliation from './views/ReportsPage/Reconciliation'
import Mail from './views/ReportsPage/Mail'
import SubscriptionsPage from './views/SubscriptionsPage'
import { isAdmin, isContentAdministrator, isCustomerAdministrator } from '@/services/PermissionService';
import DepartmentDetails from "@/views/OrganizationsPage/DepartmentDetails.vue";

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'Home',
      component: Home,
      meta: {}
    },
    {
      path: '/oidc-callback',
      name: 'oidcCallback',
      component: OidcCallback,
      meta: {
        isOidcCallback: true,
        isPublic: true
      }
    },
    {
      path: '/oidc-callback-error',
      name: 'oidcCallbackError',
      component: OidcCallbackError,
      meta: {
        isPublic: true
      }
    },
    {
      path: '/users',
      name: 'Users',
      component: Users,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/users/:id',
      name: 'UserDetails',
      component: UserDetails,
      meta: {
         requiredRole: 'useradministrators'
      }
    },
    {
      path: '/eventlog',
      name: 'EventLog',
      component: EventLog,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/orders',
      name: 'Orders',
      component: Orders,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/orders/:id',
      name: 'OrderDetails',
      component: OrderDetails,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/organizations',
      name: 'Organizations',
      component: Organizations,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
       path: '/organizations/:id',
       name: 'OrganizationDetails',
       component: OrganizationDetails,
       meta: {
         requiredRole: 'useradministrators'
       }
      },
    {
      path: '/organizations/:id/departments/:departmentId',
      name: 'DepartmentDetails',
      component: DepartmentDetails,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/licenses',
      name: 'Licenses',
      component: Licenses,
      meta: {
        requiredRole: 'useradministrators'
      }
     },
    {
      path: '/licenses/:id',
      name: 'LicenseDetails',
      component: LicenseDetails,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/reports',
      name: 'ReportsPage',
      component: ReportsPage,
      children: [
        {
          path: '/reports/reports',
          component: ReportView,
          meta: {
            requiredRole: 'admins'
          }
        },
        {
          path: '/reports/bokbasenOrders',
          component: BokbasenOrders,
          meta: {
            requiredRole: 'admins'
          }
        },
        {
          path: '/reports/biblio',
          component: BiblioOnixImport,
          meta: {
            requiredRole: 'admins'
          }
        },
        {
          path: '/reports/reconciliation',
          component: Reconciliation,
          meta: {
            requiredRole: 'admins'
          }
        },
        {
          path: '/reports/scheduledjobs',
          component: ScheduledJobs,
          meta: {
            requiredRole: 'admins'
          }
        },
        {
          path: '/reports/email',
          component: Mail,
          meta: {
            requiredRole: 'admins'
          }
        }
      ]
    },
    {
      path: '/products',
      name: 'Products',
      component: Products,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/products/:source/:sourceId',
      name: 'ProductDetails',
      component: ProductDetails,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/accessdenied',
      name: 'AccessDenied',
      component: AccessDenied,
      meta: {
        isPublic: true
      }
    },
    {
      path: '/logout',
      name: 'LogOut',
      component: LogOut,
      meta: {
        isPublic: true
      }
    },
    {
      path: '/groups',
      name: 'Groups',
      component: Groups,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/groups/:id',
      name: 'GroupDetails',
      component: GroupDetails,
      meta: {
        requiredRole: 'useradministrators'
      }
    },
    {
      path: '/subscriptions',
      name: 'Subscriptions',
      component: SubscriptionsPage,
      meta: {
        requiredRole: 'useradministrators'
      }
    }
  ]
})

const hasAccess = (requiredRole) => {
  const username = store.state.oidcStore.user?.username;
  switch (requiredRole) {
    case 'useradministrators':
      return isCustomerAdministrator(username);
    case 'admins':
      return isAdmin(username);
    default:
      return false;
  }
}

router.beforeEach(vuexOidcCreateRouterMiddleware(store, 'oidcStore'))

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.isPublic)) {
    console.log('Public page')
    next()
  } else {
    // Note that we don't check if the user is authenticated here as vuex-oidc does that for us
    if (to.meta.requiredRole) {
      if (hasAccess(to.meta.requiredRole)) {
        next();
      } else {
        next('/accessdenied');
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('Page does not require a specific role')
      }
      next()
    }
  }
})

export default router
